import { FxIdApplicationStoreCreatePaymentHandlerEmbeddingType, FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus, FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@fxid/openapi-client";
import OpenApiClient from "@fxid/openapi-client";
import { EFxIdSdkAdapterInfoSocialCapability, FxIdSdkAdapterSocialSettingDefault, FxIdSdkAdapterStickyBannerShowErrorReason } from "@fxid/sdk-core";
import { getAuthToken } from "@fxid/shared";
import { FxIdSdkBaseAdapter } from "./FxIdSdkBaseAdapter";
export class FxIdSdkAdapterForFacebookInstantGames extends FxIdSdkBaseAdapter {
    constructor(exportedSdk, game, config, dependencies) {
        super(exportedSdk, game, config, dependencies);
        this.exportedSdk = exportedSdk;
        this.game = game;
        this.config = config;
        this.adapter = "fbinstantgames";
        this.defaultSocialSettings = {
            ...FxIdSdkAdapterSocialSettingDefault,
            ui: {
                ...FxIdSdkAdapterSocialSettingDefault.ui,
                disabled: false
            }
        };
        this._fbInstantPaymentsReady = false;
    }
    async Initialize() {
        log.info("Loading facebook script");
        await new Promise((resolve) => {
            const s = document.createElement("script");
            s.type = "text/javascript";
            s.src = `https://www.facebook.com/assets.php/en_US/fbinstant.latest.js`;
            s.async = false;
            s.defer = false;
            s.crossOrigin = "anonymous";
            s.addEventListener("load", (e) => {
                log.info("Facebook instant sdk loaded");
                resolve();
            }, false);
            const head = document.getElementsByTagName("head")[0];
            head.appendChild(s);
            log.info("Facebook script loaded");
        });
        log.info("Initializing facebook instant sdk");
        await FBInstant.initializeAsync();
        log.info("Tell fb instant that game started");
        await FBInstant.startGameAsync();
        if (this.FBInstantPaymentsApisAvailable()) {
            log.info("Payments apis are available. Subscribing to on ready callback");
            FBInstant.payments.onReady(this.OnFBInstantPaymentsReady.bind(this));
        }
        else {
            log.info("Payments apis are not available");
        }
        void this.InitializeAds();
    }
    FBInstantPaymentsApisAvailable() {
        const supportedApis = FBInstant.getSupportedAPIs();
        log.info("List of supported apis: %o", supportedApis);
        // https://developers.facebook.com/docs/games/monetize/in-app-purchases#detecting-whether-payments-is-available
        const paymentsAvailable = supportedApis.includes("payments.purchaseAsync");
        return paymentsAvailable;
    }
    FBInstantRewardedVideoApiAvailable() {
        const supportedApis = FBInstant.getSupportedAPIs();
        const available = supportedApis.includes("getRewardedVideoAsync");
        return available;
    }
    FBInstantInterstitialApiAvailable() {
        const supportedApis = FBInstant.getSupportedAPIs();
        const available = supportedApis.includes("getInterstitialAdAsync");
        return available;
    }
    OnFBInstantPaymentsReady() {
        log.info("FB instant payments ready");
        this._fbInstantPaymentsReady = true;
        void this.CheckNotConsumedPurchases();
    }
    async CheckNotConsumedPurchases() {
        log.info("Checking purchases");
        const unconsumedPurchases = await FBInstant.payments.getPurchasesAsync();
        log.info("Found unconsumed purchases %o", unconsumedPurchases);
        for (const purchase of unconsumedPurchases) {
            try {
                await this.VerifyAndConsumePurchase(purchase);
            }
            catch (e) {
                log.error("Failed to verify and consume purchase: %o", purchase);
                log.error(e);
            }
        }
    }
    async GetSocialInfo() {
        const userId = await FBInstant.player.getASIDAsync();
        if (userId == null) {
            throw new Error("Failed to retrieve getASIDAsync");
        }
        const paymentsAvailable = this.FBInstantPaymentsApisAvailable();
        const result = {
            social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames,
            paymentsAvailable: paymentsAvailable,
            userId: userId,
            capabilities: [
                EFxIdSdkAdapterInfoSocialCapability.FriendsList,
                EFxIdSdkAdapterInfoSocialCapability.FriendsInvite,
                EFxIdSdkAdapterInfoSocialCapability.AdsAvailable
            ],
            displayName: FBInstant.player.getName() ?? undefined,
            socialLocale: FBInstant.getLocale() == null || FBInstant.getLocale() == ""
                ? undefined
                : (FBInstant.getLocale() ?? undefined)
        };
        const photoUrl = FBInstant.player.getPhoto();
        if (photoUrl != null) {
            result.photo = { url: photoUrl };
        }
        return result;
    }
    async BuyProduct(request) {
        if (!this._fbInstantPaymentsReady) {
            log.warn("Payments api still not ready");
            throw new Error("Payments api still not ready");
        }
        const paymentRequest = {
            Token: getAuthToken(),
            Game: this.game,
            Sku: request.sku,
            ProductNameHint: request.productNameHint,
            ProductDescriptionHint: request.productDescriptionHint,
            WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames,
            EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed
        };
        if (request.developerPayload != null) {
            paymentRequest.DeveloperPayload = {
                MerchantDeveloperPayload: request.developerPayload
            };
        }
        const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);
        log.info("Received result from server: %o", createPaymentResult);
        try {
            const productId = createPaymentResult.OrderProduct.UsedTieredProducts
                ? createPaymentResult.OrderProduct.TieredProductsSku
                : createPaymentResult.FacebookInstantGames.StoreSpecificOrderProductSku;
            const fbInstantPurchase = await FBInstant.payments.purchaseAsync({
                productID: productId,
                developerPayload: JSON.stringify({ public_order_id: createPaymentResult.TransactionId })
            });
            log.info("Received purchase from fb instant: %o", fbInstantPurchase);
            await this.VerifyAndConsumePurchase(fbInstantPurchase);
            await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
                PublicOrderId: createPaymentResult.TransactionId,
                Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
            }).catch((e) => log.error("[FBINSTANT] Failed to set optimistic status:", e));
            return {
                transactionId: createPaymentResult.TransactionId,
                stats: {
                    currency: createPaymentResult.OrderProduct.Currency ?? "",
                    currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
                    usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
                }
            };
        }
        catch (error) {
            await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
                PublicOrderId: createPaymentResult.TransactionId,
                Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
            }).catch((e) => log.error("[FBINSTANT] Failed to set optimistic status:", e));
            throw error;
        }
    }
    SocialSettings() {
        return Promise.resolve({
            ui: {
                disabled: true
            }
        });
    }
    RegisterShareHandlers() {
        return Promise.resolve();
    }
    StoreCurrency() {
        return Promise.resolve(undefined);
    }
    StatInitialize(request) {
        return Promise.resolve();
    }
    StatEvent(request) {
        return Promise.resolve();
    }
    async StoreAdditionalData() {
        if (!this.FBInstantPaymentsApisAvailable()) {
            return undefined;
        }
        const catalog = await FBInstant.payments.getCatalogAsync();
        return { catalog };
    }
    async AdsIsVideoReady() {
        if (!this.FBInstantRewardedVideoApiAvailable()) {
            return false;
        }
        return this._preloadedVideoAd != null;
    }
    async AdsShowVideo() {
        if (!this.FBInstantRewardedVideoApiAvailable()) {
            return;
        }
        if (this._preloadedVideoAd == null) {
            return;
        }
        try {
            await this._preloadedVideoAd.showAsync();
            this._preloadedVideoAd = undefined;
            this.exportedSdk.DispatchAdsFinished();
        }
        catch (e) {
            log.error("Failed to show video ad");
            log.error(e);
            this.exportedSdk.DispatchAdsFailed();
        }
    }
    async AdsIsInterstitialReady() {
        if (!this.FBInstantRewardedVideoApiAvailable()) {
            return false;
        }
        return this._preloadedInterstitialAd != null;
    }
    async AdsShowInterstitial() {
        if (!this.FBInstantRewardedVideoApiAvailable()) {
            return;
        }
        if (this._preloadedInterstitialAd == null) {
            return;
        }
        try {
            await this._preloadedInterstitialAd.showAsync();
            this._preloadedInterstitialAd = undefined;
            this.exportedSdk.DispatchAdsFinished();
        }
        catch (e) {
            log.error("Failed to show video ad");
            log.error(e);
            this.exportedSdk.DispatchAdsFailed();
        }
    }
    AdsStickyBannerShow() {
        return Promise.resolve({
            success: false,
            errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
        });
    }
    AdsStickyBannerHide() {
        return Promise.resolve({
            success: false,
            errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
        });
    }
    async GetFriends(_request) {
        const friends = [];
        try {
            const fbFriends = await FBInstant.player.getConnectedPlayersAsync();
            log.info("Fetched %s friends", fbFriends.length);
            for (const fbFriend of fbFriends) {
                const friend = {
                    displayName: fbFriend.getName() ?? undefined,
                    uid: fbFriend.getID()
                };
                const photo = fbFriend.getPhoto();
                if (photo != null) {
                    friend.photo = {
                        url: photo
                    };
                }
                friends.push(friend);
            }
        }
        catch (e) {
            log.warn(e);
        }
        return {
            friends: friends
        };
    }
    async InviteFriends(_request) {
        let image;
        // FB Instant Games требует изображение в формате base64 для отправки приглашения
        // Если изображение для приглашения не предоставлено в запросе, используем изображение по умолчанию
        if (_request.base64image) {
            image = _request.base64image;
        }
        else {
            log.debug("base64image for invite is not provided, using empty fallback");
            // TODO: Restore when asset is available
            // const { default: friendsImageInlineBase64 } = await import("./assets/Images/friends.jpg?inline") as any;
            // image = friendsImageInlineBase64;
            image = "";
        }
        const payload = {
            image,
            text: _request.inviteText,
            data: _request.trackString
        };
        await FBInstant.inviteAsync(payload);
        return { friends: [] };
    }
    AuthenticateUser(_request) {
        return Promise.resolve({});
    }
    JoinCommunity(_request) {
        return Promise.resolve({ success: false });
    }
    async AddToFavorites(_request) {
        const canCreateShortcut = await FBInstant.canCreateShortcutAsync();
        if (canCreateShortcut) {
            // Тут try/catch нормальная семантика для этого вызова
            try {
                await FBInstant.createShortcutAsync();
                return { success: true };
            }
            catch (_e) {
                return { success: false };
            }
        }
        else {
            return { success: false };
        }
    }
    WordsFilter(request) {
        return Promise.resolve({ success: true });
    }
    RateApplication(request) {
        return Promise.resolve({ success: true });
    }
    async LoadingReady() {
        return Promise.resolve();
    }
    async GameStarted() {
        return Promise.resolve();
    }
    async GameStopped() {
        return Promise.resolve();
    }
    async VerifyAndConsumePurchase(purchase) {
        log.info("Verifying payment on server %o", purchase);
        try {
            const response = await OpenApiClient.FacebookInstantGames.fxIdWebFeaturesStoreFacebookInstantGamesPurchaseEndpoint({
                Game: this.game,
                FbInstantPurchaseSignedData: purchase.signedRequest
            });
            log.info("Received response from server: %o", response);
            if (!response.Success) {
                log.error(`Purchase not validated ${JSON.stringify(purchase)}`);
            }
        }
        catch (e) {
            log.error(e);
        }
        // В ФБ нельзя НЕ закосьюмить товар
        await FBInstant.payments.consumePurchaseAsync(purchase.purchaseToken);
    }
    InitializeAds() {
        const rewardedConfig = this.config.PublicWebClientConfig.Advertisement?.FacebookInstantGames?.Rewarded;
        const interstitialConfig = this.config.PublicWebClientConfig.Advertisement?.FacebookInstantGames?.Interstitial;
        if (rewardedConfig != null) {
            void this.PreloadRewardedVideo(rewardedConfig.DefaultPlacementId);
        }
        if (interstitialConfig != null) {
            void this.PreloadInterstitial(interstitialConfig.DefaultPlacementId);
        }
    }
    async PreloadRewardedVideo(placementId) {
        if (!this.FBInstantRewardedVideoApiAvailable()) {
            log.warn("Rewarded video api not available");
        }
        try {
            const preloadedAd = await FBInstant.getRewardedVideoAsync(placementId);
            await preloadedAd.loadAsync();
            this._preloadedVideoAd = preloadedAd;
        }
        catch (e) {
            log.error("Failed to preload video ad for placement id: %s", placementId);
            log.error(e);
        }
    }
    async PreloadInterstitial(placementId) {
        if (!this.FBInstantInterstitialApiAvailable()) {
            log.warn("Interstitial api not available");
        }
        try {
            const preloadedAd = await FBInstant.getInterstitialAdAsync(placementId);
            await preloadedAd.loadAsync();
            this._preloadedInterstitialAd = preloadedAd;
        }
        catch (e) {
            log.error("Failed to preload interstitial ad for placement id: %s", placementId);
            log.error(e);
        }
    }
}
